'use client';

import { CLASSNAME, SHIPPING_SLA_TIER, formatSlaDateRange } from '@skin-seoul/service';
import type { FormattedShippingSLA } from '@skin-seoul/service';
import { Skeleton } from '@skin-seoul/design-system';

export interface ShippingSLASummaryProps {
  /** Formatted SLA payload from `formatShippingSLA` / `useShippingSLA` */
  formatted: FormattedShippingSLA;
  /** Additional wrapper classes */
  className?: string;
}

/**
 * Order-summary variant (SSD-3986 Phase 2) — Figma node 2589-35601.
 *
 * Condensed SLA surface used inside the checkout Order Summary card.
 * No icons, no pill, no confidence text — space is tight and the full block
 * already renders in the left column with the confidence caption.
 *
 * Both tiers render as a single-line range:
 *   "Expected Delivery by {earliest} – {latest}"  (measured, en dash)
 *   "Expected Delivery by {earliest} - {latest}"  (fallback, hyphen-minus)
 * When earliest === latest the helper collapses to a single date.
 */
export const ShippingSLASummary = ({ formatted, className }: ShippingSLASummaryProps) => {
  const { tier, earliestDateText, latestDateText, seasonalNoticeText } = formatted;
  const isMeasured = tier === SHIPPING_SLA_TIER.Measured;
  const separator = isMeasured ? '–' : '-';

  return (
    <div className={`flex flex-col gap-1 items-start ${CLASSNAME.NOT_TRANSLATE} ${className ?? ''}`}>
      <div className="flex gap-1 items-center text-label-small text-neutral-700 whitespace-nowrap">
        <span className="font-regular">Expected Delivery by</span>
        <span className="font-medium">
          {formatSlaDateRange(earliestDateText, latestDateText, separator)}
          {isMeasured && ' (90% of orders)'}
        </span>
      </div>
      {seasonalNoticeText && (
        <span className="text-label-small text-neutral-600 font-regular">{seasonalNoticeText}</span>
      )}
    </div>
  );
};

/** Loading skeleton matching the summary variant footprint. */
export const ShippingSLASummarySkeleton = ({ className }: { className?: string }) => (
  <div className={`flex flex-col gap-1 ${className ?? ''}`} aria-busy="true">
    <Skeleton className="h-4 w-48" />
  </div>
);
